import { authColumnHeightClassName } from '@/components/auth/authStyles';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { cn } from '@/lib/utils';

import avatarMask from '@/assets/auth/avatar-mask.svg';
import monomoLogoWhite from '@/assets/auth/monomo-logo-white.svg';
import gridBg from '@/assets/sidebar/grid-bg.png';

export interface AuthLayoutProps {
  children: React.ReactNode;
  hideFooter?: boolean;
  variant?: 'centered' | 'split';
  contentWidth?: 'default' | 'wide';
}

export interface AuthFooterProps {
  children?: React.ReactNode;
}

export const AuthFooter = ({ children }: AuthFooterProps) => {
  if (!children) {
    return null;
  }

  return <div className="mt-auto grid place-items-center gap-4 pt-4">{children}</div>;
};

function AuthPreferences({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <ThemeSwitcher className="w-auto" />
      <LanguageSwitcher className="max-w-[120px]" />
    </div>
  );
}

function AuthMarketingCard({ className }: { className?: string }) {
  return (
    <aside
      className={cn(
        'relative w-full lg:w-auto flex flex-col justify-between overflow-hidden bg-brand-secondary-bright p-8 text-white',
        className,
      )}
    >
      <svg width="0" height="0" className="absolute" aria-hidden>
        <filter id="auth-grid-knockout" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 10.2 0 -8.8"
          />
        </filter>
      </svg>
      <img
        src={gridBg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -left-[139px] top-0 size-[864px] max-w-none"
        style={{ filter: 'url(#auth-grid-knockout)' }}
      />

      <div className="relative z-10 flex flex-wrap items-center gap-3">
        <img src={monomoLogoWhite} alt="Monomo" className="h-8 w-auto" />
        <AuthPreferences className="ml-auto shrink-0 lg:hidden" />
      </div>

      <div className="relative z-10 max-w-[505px] pt-6 lg:pt-10">
        <p className="text-[40px] leading-none tracking-[-0.02em]">
          <span className="font-display">The platform that separates </span>
          <span className="font-sans">the cream from the crop.</span>
        </p>
      </div>

      <div className="relative z-10 mt-auto border-t border-white/20 pt-6 lg:pt-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div
              className="size-[60px] shrink-0 overflow-hidden bg-brand-primary"
              style={{
                WebkitMaskImage: `url(${avatarMask})`,
                maskImage: `url(${avatarMask})`,
                WebkitMaskSize: '100% 100%',
                maskSize: '100% 100%',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
              }}
            >
              <img
                src="/images/reference/dominik-taras.jpg"
                alt=""
                className="size-full object-cover object-center"
              />
            </div>
            <div>
              <p className="font-sans text-lg leading-none tracking-[-0.02em]">Dominik Taras</p>
              <p className="mt-1 font-sans text-[10px] leading-snug text-white/80">
                Business Development Executive
              </p>
            </div>
          </div>
          <p className="max-w-[210px] font-display text-lg leading-none tracking-[-0.02em] sm:text-right">
            &ldquo;Three vetted partners on the table in days. We picked fast.&rdquo;
          </p>
        </div>
      </div>
    </aside>
  );
}

export const AuthLayout = ({
  children,
  hideFooter = false,
  variant = 'split',
  contentWidth = 'default',
}: AuthLayoutProps) => {
  if (variant === 'centered') {
    return (
      <div className="grid min-h-svh content-start items-start justify-items-center gap-8 bg-white px-4 pb-[5%] pt-[5%] dark:bg-background">
        <div className="flex w-full max-w-[585px] justify-end">
          <AuthPreferences />
        </div>
        <div className="w-full max-w-[585px]">{children}</div>
        {!hideFooter && <AuthFooter />}
      </div>
    );
  }

  return (
    <div className="box-border flex min-h-svh items-safe-center justify-center overflow-y-auto bg-white dark:bg-background">
      <div className="mx-auto flex w-full min-h-svh flex-col justify-between gap-6 lg:flex-row lg:items-stretch lg:gap-10 pr-4 lg:pr-10">
        <AuthMarketingCard className="shrink-0" />

        <AuthPreferences className="fixed top-4 right-4 z-20 hidden lg:flex" />

        <div
          className={cn(
            'mx-auto flex w-full flex-col justify-center px-4 pb-8 lg:px-0 lg:pb-0',
            contentWidth === 'wide' ? 'max-w-4xl' : 'max-w-[595px]',
            authColumnHeightClassName,
          )}
        >
          {children}
          {!hideFooter && <AuthFooter />}
        </div>
      </div>
    </div>
  );
};

AuthLayout.displayName = 'AuthLayout';
